import React, { useState, useEffect, useCallback } from "react";
import {
  Calendar as CalendarIcon,
  Clock,
  MoreVertical,
  Copy,
  Edit,
  Trash2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  List,
  Video,
  Users,
  Plus,
  Radio,
  Tv,
  X,
  Wifi,
  Play,
  Youtube,
  ExternalLink,
  Link2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSiteContext } from "../../../context/SiteContext";
import {
  getScheduleMeeting,
  deleteScheduledMeeting,
  createScheduledMeeting,
  getMeetingRooms,
} from "../../../services/roomServices";

// ── SessionCard ──────────────────────────────────────────────────────
function SessionCard({ meeting, onEdit, onDelete, onCopyInvitation, onStart }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = React.useRef(null);

  const openMenu = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.right - 176 }); // 176 = w-44 (11rem)
    }
    setMenuOpen(true);
  };

  const getTimezoneId = () => {
    if (!meeting.timezone) return null;
    try {
      return meeting.timezone.split("(")[0].trim();
    } catch (e) {
      return null;
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const opts = {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    const tz = getTimezoneId();
    if (tz) {
      try { opts.timeZone = tz; } catch (e) { /* ignore */ }
    }
    return date.toLocaleTimeString("en-US", opts);
  };

  const getTimezoneAbbr = () => {
    const tz = getTimezoneId();
    if (!tz) return "";
    try {
      const parts = new Date().toLocaleTimeString("en-US", {
        timeZone: tz,
        timeZoneName: "short",
      }).split(" ");
      return parts[parts.length - 1] || "";
    } catch (e) {
      return "";
    }
  };

  const getTimeLabel = () => {
    if (!meeting.scheduledStartTime) return "";
    const start = new Date(meeting.scheduledStartTime);
    const tz = getTimezoneId();
    const opts = tz ? { timeZone: tz } : {};

    // Extract date parts in the meeting's timezone
    const startDay = new Intl.DateTimeFormat("en-US", { ...opts, day: "numeric" }).format(start);
    const startMonth = new Intl.DateTimeFormat("en-US", { ...opts, month: "numeric" }).format(start);
    const startYear = new Intl.DateTimeFormat("en-US", { ...opts, year: "numeric" }).format(start);

    const now = new Date();
    const todayDay = new Intl.DateTimeFormat("en-US", { ...opts, day: "numeric" }).format(now);
    const todayMonth = new Intl.DateTimeFormat("en-US", { ...opts, month: "numeric" }).format(now);
    const todayYear = new Intl.DateTimeFormat("en-US", { ...opts, year: "numeric" }).format(now);

    if (startDay === todayDay && startMonth === todayMonth && startYear === todayYear) {
      return "Today";
    }

    const tomorrow = new Date(now.getTime() + 86400000);
    const tomDay = new Intl.DateTimeFormat("en-US", { ...opts, day: "numeric" }).format(tomorrow);
    const tomMonth = new Intl.DateTimeFormat("en-US", { ...opts, month: "numeric" }).format(tomorrow);
    const tomYear = new Intl.DateTimeFormat("en-US", { ...opts, year: "numeric" }).format(tomorrow);

    if (startDay === tomDay && startMonth === tomMonth && startYear === tomYear) {
      return "Tomorrow";
    }

    return new Intl.DateTimeFormat("en-US", { ...opts, month: "short", day: "numeric" }).format(start);
  };

  return (
    <div className="bg-white/5 rounded-lg border border-white/10 p-3 hover:bg-white/10 transition-colors group">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 flex-1">
          <h4 className="text-sm font-medium text-white line-clamp-2">
            {meeting.roomName || "Untitled"}
          </h4>
          {meeting.status === "draft" && (
            <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded">
              Draft
            </span>
          )}
        </div>
        <div className="relative">
          <button
            ref={btnRef}
            onClick={openMenu}
            className="p-1 hover:bg-white/10 rounded opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MoreVertical className="w-4 h-4 text-gray-400" />
          </button>

          {menuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setMenuOpen(false)}
              />
              <div
                className="fixed w-52 rounded-lg shadow-xl py-1.5 z-50 bg-[#0d1117] border border-white/10"
                style={{ top: menuPos.top, left: menuPos.left }}
              >
                {meeting.status !== "draft" && (
                <button
                  onClick={() => {
                    onStart(meeting);
                    setMenuOpen(false);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-green-400 hover:bg-white/10 transition-colors"
                >
                  <Play className="w-4 h-4" />
                  Start
                </button>
                )}
                <button
                  onClick={() => {
                    onEdit(meeting);
                    setMenuOpen(false);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-white hover:bg-white/10 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    onCopyInvitation(meeting);
                    setMenuOpen(false);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-white hover:bg-white/10 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Copy Invitation
                </button>
                <button
                  onClick={() => {
                    const link = meeting.roomType === "Webinar"
                      ? `${window.location.origin}/webinar/${meeting.roomId}/event`
                      : `${window.location.origin}/${meeting.roomPermalink || meeting.roomId}`;
                    const successMsg = meeting.roomType === "Meeting"
                      ? "Meeting link copied!"
                      : "Event link copied!";
                    navigator.clipboard.writeText(link).then(() => {
                      toast.success(successMsg);
                    }).catch(() => {
                      toast.error("Failed to copy link");
                    });
                    setMenuOpen(false);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-white hover:bg-white/10 transition-colors whitespace-nowrap text-left"
                >
                  <Link2 className="w-4 h-4 flex-shrink-0" />
                  {meeting.roomType === "Meeting" ? "Copy Meeting Link" : "Copy Event Link"}
                </button>
                {meeting.roomType === "Live Stream" && (
                  <button
                    onClick={() => {
                      if (meeting.streamYouTubeBroadcastId) {
                        window.open(`https://youtube.com/live/${meeting.streamYouTubeBroadcastId}?feature=share`, "_blank");
                      } else {
                        toast.info("No YouTube Broadcast ID configured for this live stream");
                      }
                      setMenuOpen(false);
                    }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-400 hover:bg-white/10 transition-colors"
                  >
                    <Youtube className="w-4 h-4" />
                    YouTube Broadcast Link
                    <ExternalLink className="w-3 h-3 ml-auto text-gray-500" />
                  </button>
                )}
                <div className="my-1 border-t border-white/10" />
                <button
                  onClick={() => {
                    onDelete(meeting);
                    setMenuOpen(false);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-400 hover:bg-white/10 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="space-y-1 text-xs text-gray-400">
        <div className="flex items-center gap-1.5">
          <CalendarIcon className="w-3.5 h-3.5" />
          <span>{getTimeLabel()}</span>
          <span>·</span>
          <Clock className="w-3.5 h-3.5" />
          <span>{formatTime(meeting.scheduledStartTime, meeting.timezone)}</span>
        </div>

        {meeting.duration && (
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            <span>{meeting.duration} min</span>
          </div>
        )}

        {meeting.emails?.length > 0 && (
          <div className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" />
            <span>{meeting.emails.length} invitees</span>
          </div>
        )}
      </div>

      {meeting.status !== "draft" ? (
        <button
          onClick={() => onStart(meeting)}
          className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white text-xs font-semibold rounded-md hover:opacity-90 transition-opacity"
        >
          <Play className="w-3 h-3" />
          Start
        </button>
      ) : (
        <div className="mt-2 w-full flex items-center justify-center px-3 py-1.5 text-yellow-400 text-xs font-medium">
          Draft — finish setup to start
        </div>
      )}
    </div>
  );
}

// ── SessionLane ──────────────────────────────────────────────────────
function SessionLane({
  title,
  icon: Icon,
  color,
  sessions,
  onSchedule,
  onEdit,
  onDelete,
  onCopyInvitation,
  onStart,
  isLoading,
}) {
  const colorClasses = {
    purple: "from-[#6a1fbf] to-[#6a1fbf]",
    orange: "from-[#ffa500] to-[#ffa500]",
    green: "from-[#54a800] to-[#54a800]",
  };

  const bgColorClasses = {
    purple: "bg-[#6a1fbf]/10 border-[#6a1fbf]/20",
    orange: "bg-[#ffa500]/10 border-[#ffa500]/20",
    green: "bg-[#54a800]/10 border-[#54a800]/20",
  };

  return (
    <div className="flex flex-col h-full">
      <div
        className={`bg-gradient-to-r ${colorClasses[color]} rounded-t-xl p-4 flex items-center justify-between`}
      >
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-white" />
          <h3 className="font-semibold text-white">{title}</h3>
          <span className="text-white/80 text-sm">({sessions.length})</span>
        </div>
      </div>

      <div
        className={`flex-1 ${bgColorClasses[color]} border border-t-0 rounded-b-xl p-4`}
      >
        <button
          onClick={onSchedule}
          className="w-full mb-4 flex items-center justify-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Schedule {title.slice(0, -1)}
        </button>

        {isLoading ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-400">Loading...</p>
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-8">
            <Icon className="w-8 h-8 text-gray-600 mx-auto mb-2" />
            <p className="text-sm text-gray-400">
              No upcoming {title.toLowerCase()}
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
            {sessions.map((session) => (
              <SessionCard
                key={session.id}
                meeting={session}
                onEdit={onEdit}
                onDelete={onDelete}
                onCopyInvitation={onCopyInvitation}
                onStart={onStart}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Schedule Meeting Modal ───────────────────────────────────────────
function ScheduleMeetingModal({ isOpen, onClose, onSaved, defaultSessionType, rooms }) {
  const { user: modalUser } = useSiteContext();
  const isModalBusinessPlan = modalUser?.plan === "Business";
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    sessionType: "Meeting",
    title: "",
    description: "",
    roomId: "",
    startDateTime: "",
    endDateTime: "",
    duration: 60,
    invitees: [],
    recordingEnabled: false,
    requireRegistration: false,
    youtubeEnabled: false,
    youtubeUrl: "rtmp://a.rtmp.youtube.com/live2",
    youtubeKey: "",
    youtubeBroadcastId: "",
    facebookEnabled: false,
    facebookUrl: "rtmps://live-api-s.facebook.com:443/rtmp/",
    facebookKey: "",
  });
  const [newInviteeEmail, setNewInviteeEmail] = useState("");
  const [newInviteeName, setNewInviteeName] = useState("");

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      now.setMinutes(0, 0, 0);
      now.setHours(now.getHours() + 1);
      const end = new Date(now.getTime() + 60 * 60 * 1000);

      const formatLocalDateTime = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };

      setFormData({
        sessionType: defaultSessionType || "Meeting",
        title: "",
        description: "",
        roomId: rooms.length > 0 ? (rooms[0].id || rooms[0]._id || "") : "",
        startDateTime: formatLocalDateTime(now),
        endDateTime: formatLocalDateTime(end),
        duration: 60,
        invitees: [],
        recordingEnabled: false,
        requireRegistration: false,
        youtubeEnabled: false,
        youtubeUrl: "rtmp://a.rtmp.youtube.com/live2",
        youtubeKey: "",
        youtubeBroadcastId: "",
        facebookEnabled: false,
        facebookUrl: "rtmps://live-api-s.facebook.com:443/rtmp/",
        facebookKey: "",
      });
      setNewInviteeEmail("");
      setNewInviteeName("");
    }
  }, [isOpen, defaultSessionType, rooms]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // Auto-calculate end time when start changes
      if (field === "startDateTime" && value) {
        const start = new Date(value);
        const end = new Date(start.getTime() + prev.duration * 60 * 1000);
        const year = end.getFullYear();
        const month = String(end.getMonth() + 1).padStart(2, "0");
        const day = String(end.getDate()).padStart(2, "0");
        const hours = String(end.getHours()).padStart(2, "0");
        const minutes = String(end.getMinutes()).padStart(2, "0");
        updated.endDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      // Auto-calculate duration when end changes
      if (field === "endDateTime" && value && prev.startDateTime) {
        const start = new Date(prev.startDateTime);
        const end = new Date(value);
        const diffMinutes = Math.round((end - start) / (1000 * 60));
        if (diffMinutes <= 0) {
          toast.error("End time must be after start time");
          return prev;
        }
        updated.duration = diffMinutes;
      }

      return updated;
    });
  };

  const addInvitee = () => {
    if (!newInviteeEmail.trim()) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newInviteeEmail)) {
      toast.error("Please enter a valid email");
      return;
    }
    if (formData.invitees.some((inv) => inv.email === newInviteeEmail)) {
      toast.error("This email is already added");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      invitees: [
        ...prev.invitees,
        { email: newInviteeEmail.trim(), name: newInviteeName.trim() },
      ],
    }));
    setNewInviteeEmail("");
    setNewInviteeName("");
  };

  const removeInvitee = (email) => {
    setFormData((prev) => ({
      ...prev,
      invitees: prev.invitees.filter((inv) => inv.email !== email),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.startDateTime) {
      toast.error("Start date & time is required");
      return;
    }
    if (
      formData.endDateTime &&
      new Date(formData.endDateTime) <= new Date(formData.startDateTime)
    ) {
      toast.error("End time must be after start time");
      return;
    }

    setSaving(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const submitData = new FormData();
      submitData.append("roomName", formData.title);
      submitData.append("roomType", formData.sessionType);
      submitData.append("startTime", formData.startDateTime);
      submitData.append("duration", String(formData.duration));
      submitData.append("timezone", timezone);
      submitData.append("allowChat", "true");
      submitData.append("allowScreenSharing", "true");
      submitData.append("muteParticipantsOnEntry", "true");
      submitData.append("allowReactions", "true");
      submitData.append("allowRaiseHand", "true");
      submitData.append("requirePassword", "false");
      submitData.append("hostApprovalRequired", "true");

      if (formData.description) {
        submitData.append("description", formData.description);
      }

      // Associate with selected room for Meetings (not Webinars)
      if (formData.sessionType === "Meeting" && formData.roomId) {
        submitData.append("associatedRoomId", formData.roomId);
      }

      // Send invitee emails as JSON array (legacy field)
      // Also send the full invitees list (with names) so the backend can personalize emails.
      if (formData.invitees.length > 0) {
        const emailsList = formData.invitees.map((inv) => inv.email);
        submitData.append("emails", JSON.stringify(emailsList));
        submitData.append(
          "invitees",
          JSON.stringify(formData.invitees.map((inv) => ({ email: inv.email, name: inv.name || "" })))
        );
      }

      // Streaming fields for Live Stream type
      if (formData.sessionType === "Live Stream") {
        if (formData.youtubeEnabled && formData.youtubeKey) {
          submitData.append("streamYouTubeUrl", formData.youtubeUrl);
          submitData.append("streamYouTubeKey", formData.youtubeKey);
          if (formData.youtubeBroadcastId) {
            submitData.append("streamYouTubeBroadcastId", formData.youtubeBroadcastId);
          }
        }
        if (formData.facebookEnabled && formData.facebookKey) {
          submitData.append("streamFacebookUrl", formData.facebookUrl);
          submitData.append("streamFacebookKey", formData.facebookKey);
        }
      }

      await createScheduledMeeting(submitData);
      toast.success(
        `${formData.sessionType} scheduled successfully`
      );
      onClose();
      onSaved();
    } catch (error) {
      console.error("Error scheduling meeting:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to schedule meeting";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const sessionTypeLabel =
    formData.sessionType === "Live Stream"
      ? "Live Stream"
      : formData.sessionType;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[#001233] rounded-2xl border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-0">
          <h2 className="text-xl font-bold text-white">
            Schedule {sessionTypeLabel}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Session Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Session Type
            </label>
            <div className="relative">
              <select
                value={formData.sessionType}
                onChange={(e) => handleChange("sessionType", e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23fff' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 1rem center",
                }}
              >
                <option value="Meeting" style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>Meeting</option>
                <option value="Live Stream" disabled={!isModalBusinessPlan} style={{ backgroundColor: '#1a1a2e', color: !isModalBusinessPlan ? '#666' : '#fff' }}>Live Stream</option>
              </select>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              placeholder={
                formData.sessionType === "Webinar"
                  ? "Product Launch Webinar"
                  : formData.sessionType === "Live Stream"
                  ? "Live Q&A Session"
                  : "Weekly Team Sync"
              }
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description (optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Discuss project updates and next steps..."
              rows={3}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors resize-y"
            />
          </div>

          {/* Room */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Room
            </label>
            <select
              value={formData.roomId}
              onChange={(e) => handleChange("roomId", e.target.value)}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors appearance-none cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23fff' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 1rem center",
              }}
            >
              {rooms.length === 0 && (
                <option value="" style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>No rooms available</option>
              )}
              {rooms.map((room) => (
                <option key={room.id || room._id} value={room.id || room._id} style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>
                  {room.roomName || room.name || "Unnamed Room"}
                </option>
              ))}
            </select>
          </div>

          {/* Start & End Date/Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Start Date & Time
              </label>
              <input
                type="datetime-local"
                value={formData.startDateTime}
                onChange={(e) => handleChange("startDateTime", e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors [color-scheme:dark]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                End Date & Time
              </label>
              <input
                type="datetime-local"
                value={formData.endDateTime}
                min={formData.startDateTime || undefined}
                onChange={(e) => handleChange("endDateTime", e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors [color-scheme:dark]"
              />
            </div>
          </div>
          <p className="text-xs text-gray-400 -mt-2">
            Timezone: {(() => {
              const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
              const abbr = new Date().toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
              return `${abbr} (${tz})`;
            })()}
          </p>

          {/* Invitees */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Invitees
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="Email"
                value={newInviteeEmail}
                onChange={(e) => setNewInviteeEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addInvitee();
                  }
                }}
                className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors"
              />
              <input
                type="text"
                placeholder="Name (optional)"
                value={newInviteeName}
                onChange={(e) => setNewInviteeName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addInvitee();
                  }
                }}
                className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors"
              />
              <button
                onClick={addInvitee}
                className="px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white hover:bg-white/10 transition-colors"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            {formData.invitees.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {formData.invitees.map((inv) => (
                  <span
                    key={inv.email}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 text-white text-sm rounded-full"
                  >
                    {inv.name || inv.email}
                    <button
                      onClick={() => removeInvitee(inv.email)}
                      className="hover:text-red-400 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Toggle Options */}
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3.5 rounded-lg bg-white/5">
              <span className="text-sm font-medium text-white">
                Enable recording
              </span>
              <button
                onClick={() =>
                  handleChange("recordingEnabled", !formData.recordingEnabled)
                }
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  formData.recordingEnabled ? "bg-[#6a1fbf]" : "bg-gray-600"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                    formData.recordingEnabled ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            {/* Require registration — hidden until properly supported */}
          </div>

          {/* Streaming Destinations (Live Stream only) */}
          {formData.sessionType === "Live Stream" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Wifi className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-semibold text-white">
                  Streaming Destinations
                </span>
              </div>

              {/* YouTube */}
              <div className="border border-white/10 rounded-lg p-4 bg-white/5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-red-600 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-white">YouTube</span>
                  </div>
                  <button
                    onClick={() => handleChange("youtubeEnabled", !formData.youtubeEnabled)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      formData.youtubeEnabled ? "bg-red-600" : "bg-gray-600"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                        formData.youtubeEnabled ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
                {formData.youtubeEnabled && (
                  <div className="space-y-2 mt-3">
                    <input
                      type="text"
                      placeholder="RTMP URL (e.g., rtmp://a.rtmp.youtube.com/live2)"
                      value={formData.youtubeUrl}
                      onChange={(e) => handleChange("youtubeUrl", e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-white/20"
                    />
                    <input
                      type="password"
                      placeholder="Stream Key"
                      value={formData.youtubeKey}
                      onChange={(e) => handleChange("youtubeKey", e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-white/20"
                    />
                    <input
                      type="text"
                      placeholder="Broadcast ID (e.g., bM_H9ekdv2Y)"
                      value={formData.youtubeBroadcastId}
                      onChange={(e) => handleChange("youtubeBroadcastId", e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-white/20"
                    />
                  </div>
                )}
              </div>

              {/* Facebook */}
              <div className="border border-white/10 rounded-lg p-4 bg-white/5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-blue-600 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-white">Facebook</span>
                  </div>
                  <button
                    onClick={() => handleChange("facebookEnabled", !formData.facebookEnabled)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      formData.facebookEnabled ? "bg-blue-600" : "bg-gray-600"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                        formData.facebookEnabled ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
                {formData.facebookEnabled && (
                  <div className="space-y-2 mt-3">
                    <input
                      type="text"
                      placeholder="RTMP URL (e.g., rtmps://live-api-s.facebook.com:443/rtmp/)"
                      value={formData.facebookUrl}
                      onChange={(e) => handleChange("facebookUrl", e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-white/20"
                    />
                    <input
                      type="password"
                      placeholder="Stream Key"
                      value={formData.facebookKey}
                      onChange={(e) => handleChange("facebookKey", e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-white/20"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 pt-0">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !formData.title.trim()}
            className="px-5 py-2.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            {saving
              ? "Scheduling..."
              : `Schedule ${sessionTypeLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Schedule Component ──────────────────────────────────────────
const Schedule = () => {
  const navigate = useNavigate();
  const { user } = useSiteContext();
  const isBusinessPlan = user?.plan === "Business";
  const [view, setView] = useState("list");
  const [meetings, setMeetings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage] = useState(1);
  const [rowsPerPage] = useState(50);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [meetingToDelete, setMeetingToDelete] = useState(null);

  // Schedule modal state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleModalType, setScheduleModalType] = useState("Meeting");

  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());

  // Fetch scheduled meetings
  const fetchMeetings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getScheduleMeeting(currentPage, rowsPerPage);
      const meetingsData = response.data || [];
      setMeetings(meetingsData);
    } catch (err) {
      console.error("Error fetching meetings:", err);
      toast.error("Failed to load scheduled meetings");
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage]);

  // Fetch rooms for the dropdown
  const fetchRooms = useCallback(async () => {
    try {
      const response = await getMeetingRooms(1, 100, true);
      const roomsData = response.data || [];
      setRooms(roomsData);
    } catch (err) {
      console.error("Error fetching rooms:", err);
    }
  }, []);

  useEffect(() => {
    fetchMeetings();
    fetchRooms();
  }, [fetchMeetings, fetchRooms]);

  const handleCopyInvitation = (meeting) => {
    let timezoneLabel = "";
    if (meeting.timezone) {
      try {
        const tzId = meeting.timezone.split("(")[0].trim();
        const parts = new Date(meeting.scheduledStartTime).toLocaleTimeString("en-US", {
          timeZone: tzId,
          timeZoneName: "short",
        }).split(" ");
        timezoneLabel = ` ${parts[parts.length - 1]}`;
      } catch {}
    }
    const joinLink = meeting.roomType === "Webinar"
      ? `${window.location.origin}/webinar/${meeting.roomId}/event`
      : `${window.location.origin}/${meeting.roomPermalink || meeting.roomId}`;
    const inviteText = `You're invited to: ${meeting.roomName}\n\nType: ${meeting.roomType}\nDate: ${formatDate(meeting.scheduledStartTime)}\nTime: ${formatTime(meeting.scheduledStartTime)}${timezoneLabel}\n\nJoin here: ${joinLink}`;
    navigator.clipboard.writeText(inviteText);
    toast.success("Invitation copied to clipboard");
  };

  const handleStartMeeting = (meeting) => {
    const slug = meeting.roomPermalink || meeting.roomId;
    if (slug) {
      navigate(`/${slug}`);
    } else {
      toast.error("Room ID not found for this meeting");
    }
  };

  const handleEditMeeting = (meeting) => {
    if (meeting.roomType === "Webinar") {
      navigate(`/dashboard/edit-webinar/${meeting.id}`);
    } else {
      navigate(`/dashboard/edit-schedule-meeting/${meeting.id}`);
    }
  };

  const handleDeleteMeeting = (meeting) => {
    setMeetingToDelete(meeting);
    setShowDeleteModal(true);
  };

  const confirmDeleteMeeting = async () => {
    if (!meetingToDelete) return;

    try {
      await deleteScheduledMeeting(meetingToDelete.id);
      fetchMeetings();
      setShowDeleteModal(false);
      setMeetingToDelete(null);
      toast.success("Meeting deleted successfully");
    } catch (err) {
      console.error("Error deleting meeting:", err);
      toast.error("Error deleting meeting");
    }
  };

  const cancelDeleteMeeting = () => {
    setShowDeleteModal(false);
    setMeetingToDelete(null);
  };

  const openScheduleModal = (type) => {
    setScheduleModalType(type);
    setShowScheduleModal(true);
  };

  // Format date
  const formatDate = (dateString, tz) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      timeZone: tz || undefined,
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  // Format time (with timezone)
  const formatTime = (dateString, tz) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      timeZone: tz || undefined,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });
  };

  // Get meeting type color
  const getMeetingTypeColor = (type) => {
    switch (type) {
      case "Meeting":
        return "bg-[#6a1fbf] text-white";
      case "Webinar":
        return "bg-[#ffa500] text-white";
      case "Live Stream":
        return "bg-[#00c853] text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  // Filter meetings by type, ordered so the host's next session is at the top.
  // Partitioning rule (in order):
  //   1. Upcoming sessions (scheduledStartTime >= now), sorted ASC by start
  //      time → the very next one to start is row 1.
  //   2. Past sessions (scheduledStartTime < now), sorted DESC by start time
  //      → most recently held first; older history sinks toward the bottom.
  //   3. Drafts / sessions with no scheduledStartTime go last.
  // Sorting only the type-filtered list keeps each lane independent and
  // avoids re-rendering cost on the full meetings array.
  const getMeetingsByType = (type) => {
    const filtered = meetings.filter((m) => m.roomType === type);
    const nowMs = Date.now();
    const upcoming = [];
    const past = [];
    const undated = [];
    for (const m of filtered) {
      const t = m.scheduledStartTime ? new Date(m.scheduledStartTime).getTime() : NaN;
      if (Number.isNaN(t)) undated.push(m);
      else if (t >= nowMs) upcoming.push(m);
      else past.push(m);
    }
    upcoming.sort((a, b) => new Date(a.scheduledStartTime) - new Date(b.scheduledStartTime));
    past.sort((a, b) => new Date(b.scheduledStartTime) - new Date(a.scheduledStartTime));
    return [...upcoming, ...past, ...undated];
  };

  const meetingsList = getMeetingsByType("Meeting");
  const webinarsList = getMeetingsByType("Webinar");
  const liveStreamsList = getMeetingsByType("Live Stream");

  // Calendar functions
  const getMeetingsForDate = (date) => {
    return meetings.filter((meeting) => {
      if (!meeting.scheduledStartTime) return false;
      const meetingDate = new Date(meeting.scheduledStartTime);
      return (
        meetingDate.getDate() === date.getDate() &&
        meetingDate.getMonth() === date.getMonth() &&
        meetingDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const navigateMonth = (direction) => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setMonth(prevDate.getMonth() + direction);
      return newDate;
    });
  };

  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Fill from start of week (Sunday) before month start
    const startOffset = firstDay.getDay();
    const calStart = new Date(year, month, 1 - startOffset);

    // Fill to end of week (Saturday) after month end
    const endOffset = 6 - lastDay.getDay();
    const calEnd = new Date(year, month + 1, endOffset);

    const days = [];
    const d = new Date(calStart);
    while (d <= calEnd) {
      days.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return days;
  };

  const isSameDay = (a, b) =>
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear();

  const isSameMonth = (a, b) =>
    a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

  const getSessionColor = (type) => {
    switch (type) {
      case "Webinar":
        return "bg-blue-500/20 border-blue-500/50 text-blue-400";
      case "Live Stream":
        return "bg-red-500/20 border-red-500/50 text-red-400";
      default:
        return "bg-purple-500/20 border-purple-500/50 text-purple-400";
    }
  };

  const getSessionIcon = (type) => {
    switch (type) {
      case "Webinar":
        return <Radio className="w-3 h-3 flex-shrink-0" />;
      case "Live Stream":
        return <Tv className="w-3 h-3 flex-shrink-0" />;
      default:
        return <Video className="w-3 h-3 flex-shrink-0" />;
    }
  };

  return (
    <div className="space-y-6 p-3 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Schedule</h2>
          <p className="text-gray-400 mt-1">Create and manage your sessions</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setView("calendar")}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-all ${
              view === "calendar"
                ? "bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white"
                : "bg-white/5 border border-white/10 text-white hover:bg-white/10"
            }`}
          >
            <CalendarIcon className="w-4 h-4" />
            Calendar
          </button>
          <button
            onClick={() => setView("list")}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-all ${
              view === "list"
                ? "bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white"
                : "bg-white/5 border border-white/10 text-white hover:bg-white/10"
            }`}
          >
            <List className="w-4 h-4" />
            List
          </button>
        </div>
      </div>

      {/* List View - Three Lanes */}
      {view === "list" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <SessionLane
            title="Meetings"
            icon={Video}
            color="purple"
            sessions={meetingsList}
            onSchedule={() => openScheduleModal("Meeting")}
            onEdit={handleEditMeeting}
            onDelete={handleDeleteMeeting}
            onCopyInvitation={handleCopyInvitation}
            onStart={handleStartMeeting}
            isLoading={loading}
          />

          <div className={!isBusinessPlan ? "opacity-50 pointer-events-none" : ""}>
            <SessionLane
              title="Webinars"
              icon={Radio}
              color="orange"
              sessions={webinarsList}
              onSchedule={() => navigate("/dashboard/webinar-detail")}
              onEdit={handleEditMeeting}
              onDelete={handleDeleteMeeting}
              onCopyInvitation={handleCopyInvitation}
              onStart={handleStartMeeting}
              isLoading={loading}
            />
          </div>

          <div className={!isBusinessPlan ? "opacity-50 pointer-events-none" : ""}>
            <SessionLane
              title="Live Streams"
              icon={Tv}
              color="green"
              sessions={liveStreamsList}
              onSchedule={() => openScheduleModal("Live Stream")}
              onEdit={handleEditMeeting}
              onDelete={handleDeleteMeeting}
              onCopyInvitation={handleCopyInvitation}
              onStart={handleStartMeeting}
              isLoading={loading}
            />
          </div>
        </div>
      )}

      {/* Calendar View */}
      {view === "calendar" && (
        <div className="space-y-4">
          {/* Calendar Header */}
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-white">
              {currentDate.toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentDate(new Date())}
                className="px-3 py-1.5 bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-md text-sm transition-colors"
              >
                Today
              </button>
              <button
                onClick={() => navigateMonth(-1)}
                className="p-1.5 bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-md transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigateMonth(1)}
                className="p-1.5 bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-md transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
            {/* Day Headers */}
            <div className="grid grid-cols-7 border-b border-white/10">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div
                  key={day}
                  className="p-2 text-center text-sm font-medium text-gray-400 bg-white/5"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7">
              {getCalendarDays().map((day) => {
                const dayMeetings = getMeetingsForDate(day);
                const isCurrentMonth = isSameMonth(day, currentDate);
                const isDayToday = isSameDay(day, new Date());

                return (
                  <div
                    key={day.toISOString()}
                    className={`min-h-[120px] p-2 border-r border-b border-white/10 ${
                      !isCurrentMonth ? "bg-white/[0.02]" : ""
                    } ${isDayToday ? "bg-purple-500/10" : ""} hover:bg-white/5 transition-colors cursor-pointer`}
                    onClick={() =>
                      isCurrentMonth && openScheduleModal("Meeting")
                    }
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className={
                          !isCurrentMonth
                            ? "text-sm font-medium text-gray-600"
                            : isDayToday
                            ? "text-xs font-medium text-white bg-purple-600 w-6 h-6 rounded-full flex items-center justify-center"
                            : "text-sm font-medium text-gray-300"
                        }
                      >
                        {day.getDate()}
                      </span>
                      {isCurrentMonth && dayMeetings.length === 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openScheduleModal("Meeting");
                          }}
                          className="opacity-0 hover:opacity-100 transition-opacity p-0.5 hover:bg-white/10 rounded"
                        >
                          <Plus className="w-3 h-3 text-gray-400" />
                        </button>
                      )}
                    </div>

                    {/* Meetings */}
                    <div className="space-y-1">
                      {dayMeetings.slice(0, 3).map((meeting) => (
                        <div
                          key={meeting.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditMeeting(meeting);
                          }}
                          className={`p-1.5 rounded border text-xs truncate cursor-pointer hover:brightness-125 transition-all ${getSessionColor(
                            meeting.roomType
                          )}`}
                          title={meeting.roomName}
                        >
                          <div className="flex items-center gap-1">
                            {getSessionIcon(meeting.roomType)}
                            <span className="truncate">
                              {formatTime(meeting.scheduledStartTime)}{" "}
                              {meeting.roomName}
                            </span>
                          </div>
                        </div>
                      ))}
                      {dayMeetings.length > 3 && (
                        <div className="text-xs text-gray-400 pl-1">
                          +{dayMeetings.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-6 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-purple-500/20 border border-purple-500/50"></div>
              <span className="text-gray-400">Meeting</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-blue-500/20 border border-blue-500/50"></div>
              <span className="text-gray-400">Webinar</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-red-500/20 border border-red-500/50"></div>
              <span className="text-gray-400">Live Stream</span>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Meeting Modal */}
      <ScheduleMeetingModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onSaved={fetchMeetings}
        defaultSessionType={scheduleModalType}
        rooms={rooms}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#001233] rounded-2xl border border-white/10 p-6 md:p-8 w-full max-w-md">
            <div className="flex flex-col items-center text-center">
              <AlertTriangle className="h-12 w-12 md:h-16 md:w-16 text-red-500 mb-3 md:mb-4" />
              <h3 className="text-lg md:text-xl font-bold text-white mb-2">
                Delete Meeting?
              </h3>
              <p className="text-sm md:text-base text-gray-300 mb-4 md:mb-6">
                Are you sure you want to delete "{meetingToDelete?.roomName}"?
                This action cannot be undone.
              </p>
              <div className="flex flex-col md:flex-row justify-center gap-3 md:gap-4 w-full md:w-auto">
                <button
                  onClick={cancelDeleteMeeting}
                  className="w-full md:w-auto px-6 py-2.5 md:py-2 rounded-lg bg-white/10 border border-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteMeeting}
                  className="w-full md:w-auto px-6 py-2.5 md:py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedule;
